import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { BoxCenterStart } from '../style/box';
// import from MUI
import Grid from '@mui/material/Grid2';
import { Send } from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import { Alert } from "@mui/material";

import { useAuth } from "../contexts/AuthContext";
import {
    CalloutStep,
} from "../components/workflow_step";
import CalloutMemberList from '../components/callout_member_list';
import TaskAssignment from '../components/task_assignment';
import { RegisterButton } from '../style/button';
import { fetchData, postData } from "../func/api";
import SkeletonLoading from "../components/skeleton_loading";
import { convertTaskAssignmentsToApiFormat } from "../func/util";
import { BaseDialog } from '../components/base_dialog';
import { InputDialog } from "../components/input_dialog";
import { set } from "react-hook-form";


const Callout = () => {
    const { currentUser } = useAuth();
    const intervalRef = useRef(null);

    // State
    const [earthquake, setEarthquake] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [assignedList, setAssignedList] = useState([])
    const [damWorkAssignList, setDamWorkAssignList] = useState([])
    const [powerplantWorkAssignList, setPowerplantWorkAssignList] = useState([])
    const [damWork, setDamWork] = useState([]);
    const [powerpointWork, setPowerpointWork] = useState([]);
    const [damMessage, setDamMessage] = useState([]);
    const [powerpointMessage, setPowerpointMessage] = useState([]);
    const [unanswerUserList, setUnanswerUserList] = useState([]);
    const [calloutUserList, setCalloutUserList] = useState([]);
    const [noAssignUserList, setNoAssignUserList] = useState([]);
    const [taskAssignments, setTaskAssignments] = useState({});
    const [isCalloutSent, setIsCalloutSent] = useState(false);
    const { uuid } = useParams();
    const [loading, setLoading] = useState(true);
    const [notifyTargetWorkUuids, setNotifyTargetWorkUuids] = useState([]);

    // ダイアログ管理用のstate
    const [dialogState, setDialogState] = useState({
        open: false,
        type: null,
        title: '',
        message: '',
        onConfirm: null
    });
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyComment, setNotifyComment] = useState("");
    const [notifyCheckState, setNotifyCheckState] = useState({});   

    const fetchDataAndUpdateState = useCallback(async () => {
        if (!uuid) return;

        try {
            // 地震情報、ダムまたは発電所業務担当者一覧、ダムまたは発電所対応状況一覧を並行して取得
            const earthquake = await fetchData(`api/get_earthquake/${uuid}`);
            const [damAssignList, powerplantAssignList, damWork, powerpointWork, unanswerUser, calloutUser, noAssignUser, initialtaskUsers, calloutStatus] = await Promise.all([
                fetchData(`api/get_work_assign_list/${uuid}/1`),
                fetchData(`api/get_work_assign_list/${uuid}/2`),
                fetchData(`api/get_work/1`),
                fetchData(`api/get_work/2`),
                fetchData(`api/get_unanswer_user_list/${earthquake.report_uuid}`),
                fetchData(`api/get_callout/${earthquake.report_uuid}`),
                fetchData(`api/get_work_noassign_list/${earthquake.report_uuid}`),
                fetchData(`api/get_report_user_details/${earthquake.report_uuid}`),
                fetchData(`api/get_all_disabled_user_sms_complete/${earthquake.report_uuid}/${currentUser.id}`)
            ]);
            setEarthquake(earthquake);
            setAssignedList([...damAssignList, ...powerplantAssignList]);
            setDamWork(damWork);
            setPowerpointWork(powerpointWork);
            setUnanswerUserList(unanswerUser);
            setCalloutUserList(calloutUser || []);
            setIsCalloutSent(calloutStatus.all_disabled_users_have_sms_sent || false)


            const allUsers = [
                ...initialtaskUsers.dam.map(user => ({ 
                    full_name: user.full_name, 
                    uuid: user.uuid 
                })),
                ...initialtaskUsers.powerplant.map(user => ({ 
                    full_name: user.full_name, 
                    uuid: user.uuid 
                }))
            ];
            setNoAssignUserList([...noAssignUser, ...allUsers]);

            const damUser = initialtaskUsers.dam.length > 0
                ? `ダム対応中 (担当者：${initialtaskUsers.dam.map(user => user.full_name).join('、')})`
                : '';

            const powerplantUser = initialtaskUsers.powerplant.length > 0
                ? `発電所対応中 (担当者：${initialtaskUsers.powerplant.map(user => user.full_name).join('、')})`
                : '';
            
            setDamMessage(damUser);
            setPowerpointMessage(powerplantUser);
        } catch (error) {
            console.error("データ取得中にエラーが発生しました:", error);
        }
    }, [
        uuid,
        setEarthquake, setDamWork, setPowerpointWork, setUnanswerUserList, setCalloutUserList, setNoAssignUserList
    ]);

    useEffect(() => {
        if (!uuid) return;
        let mounted = true;
        // 初回ロード時のデータ取得（ローディング状態の管理を含む）
        const initialFetch = async () => {
            setLoading(true);
            try {
                await fetchDataAndUpdateState();
            } catch (error) {
                // fetchDataAndUpdateState 内でエラー処理済み
            } finally {
                setLoading(false);
            }
        };
        initialFetch();
        return () => {
            mounted = false;
        };
    }, [uuid, fetchDataAndUpdateState]);


    useEffect(() => {
        const TEN_SECONDS = 0.1 * 60 * 1000;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!isNotifyDialogOpen) {
            // ダイアログ閉のときだけ定期更新をセット
            intervalRef.current = setInterval(() => {
                fetchDataAndUpdateState();
            }, TEN_SECONDS);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [fetchDataAndUpdateState, isNotifyDialogOpen]);

    const enhancedDamWork = damWork.map(work => {
        const dbAssignment = damWorkAssignList.find(item => item.work_uuid === work.uuid);

        return {
            ...work,
            // キーが存在する場合のみtaskAssignmentsの値を使用、存在しない場合はDB登録済みの値
            value1: `dam-${work.uuid}-1` in taskAssignments
                ? taskAssignments[`dam-${work.uuid}-1`]
                : (dbAssignment?.user_uuid01 && !dbAssignment?.is_assign_sent01 ? dbAssignment.user_uuid01 : undefined),
            value2: `dam-${work.uuid}-2` in taskAssignments
                ? taskAssignments[`dam-${work.uuid}-2`]
                : (dbAssignment?.user_uuid02 && !dbAssignment?.is_assign_sent02 ? dbAssignment.user_uuid02 : undefined),
            notify_checked: notifyCheckState[work.uuid] || false
        };
    });

    const enhancedPowerpointWork = powerpointWork.map(work => {
        const dbAssignment = powerplantWorkAssignList.find(item => item.work_uuid === work.uuid);

        return {
            ...work,
            value1: `powerpoint-${work.uuid}-1` in taskAssignments
                ? taskAssignments[`powerpoint-${work.uuid}-1`]
                : (dbAssignment?.user_uuid01 && !dbAssignment?.is_assign_sent01 ? dbAssignment.user_uuid01 : undefined),
            value2: `powerpoint-${work.uuid}-2` in taskAssignments
                ? taskAssignments[`powerpoint-${work.uuid}-2`]
                : (dbAssignment?.user_uuid02 && !dbAssignment?.is_assign_sent02 ? dbAssignment.user_uuid02 : undefined),
            notify_checked: notifyCheckState[work.uuid] || false
        };
    });

    const handleNotifyChange = (workUuid, isChecked, category) => {
        // チェックボックス状態を更新
        setNotifyCheckState(prev => ({
            ...prev,
            [workUuid]: isChecked
        }));
        const [targetTaskList, setTargetTaskList] = category === 'dam'
            ? [damWork, setDamWork]
            : [powerpointWork, setPowerpointWork];

        const newTaskList = targetTaskList.map(row => {
            if (row.uuid === workUuid) {
                return {
                    ...row,
                    notify_checked: isChecked // チェック状態を更新
                };
            }
            return row;
        });
        setTargetTaskList(newTaskList);
    };

    const handleAssignmentChange = (workUuid, userSlot, selectedUserUuid, category) => {
        // 状態を更新（画面に反映）
        setTaskAssignments(prev => ({
            ...prev,
            [`${category}-${workUuid}-${userSlot}`]: selectedUserUuid
        }));
    };

    const openDialog = (type, title, message, onConfirm) => {
        setDialogState({
            open: true,
            type,
            title,
            message,
            onConfirm
        });
    };

    const closeDialog = () => {
        setDialogState({
            ...dialogState,
            open: false
        });
    };

    const handleDialogConfirm = async () => {
        if (dialogState.onConfirm) {
            await dialogState.onConfirm();
        }
        closeDialog();
    };

    const openNotifyDialog = () => {
        setNotifyComment("");
        setIsNotifyDialogOpen(true);
    };

    const closeNotifyDialog = () => {
        setIsNotifyDialogOpen(false);
        fetchDataAndUpdateState();
        setNotifyCheckState({});
    };

    const onCallout = async () => {
        try {
            const response = await postData(`api/send_sms_callout/${uuid}/${earthquake.report_uuid}/${currentUser.id}`);
            setAlertMessage("要因招集SMSの送信を行いました");
            setAlertSeverity("success");
            setAlertOpen(true);
            setIsCalloutSent(true);
        } catch (error) {
            let errorMessage = "SMSの送信に失敗しました。時間をおいて再度お試しください。";
            if (error.response?.data?.error) {
                // バックエンドの jsonify({"error": message}) からメッセージを取得し、そのまま使用する
                errorMessage = error.response.data.error;
            } else if (error.message) {
                // API通信エラーなど、バックエンド由来でないエラーメッセージ
                errorMessage = error.message;
            }
            setAlertMessage(errorMessage);
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    // 要員招集ボタン（ダイアログ表示）
    const onCalloutClick = () => {
        openDialog(
            'callout',
            '要員招集の確認',
            '要員招集SMSを送信します。よろしいですか？',
            onCallout
        );
    };

    const handleSave = async () => {
        const assignmentData = convertTaskAssignmentsToApiFormat(taskAssignments, earthquake);
        try {
            const response = await postData(`api/post_report_work`, { assignments: assignmentData })
            setAlertMessage("業務割当を保存しました");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (error) {
            const errorMessage = "業務割当の保存に失敗しました。時間をおいて再度お試しください。";
            setAlertMessage(errorMessage);
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    const handleSaveClick = () => {
        openDialog(
            'save',
            '業務割当の保存',
            '業務割当を保存します。よろしいですか？',
            handleSave
        );
    };

    const handleNotify = async () => {
        try {
            const payload = {
                earthquake_uuid: earthquake.uuid,
                report_uuid: earthquake.report_uuid,
                comment: notifyComment,
                work_uuids: notifyTargetWorkUuids
            };
            // バックエンドに業務UUIDのリストを送信
            const response = await postData(`api/send_sms_assign/${currentUser.id}`, payload);
            setAlertMessage("担当者に通知を送信しました");
            setAlertSeverity("success");
            setAlertOpen(true);

            closeNotifyDialog();
        } catch (error) {
            let errorMessage = "通知の送信に失敗しました。時間をおいて再度お試しください。";
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            setAlertMessage(errorMessage);
            setAlertSeverity("error");
            setAlertOpen(true);

            console.error("通知エラー:", error);
        }
    };

    const handleNotifyClick = () => {
        // チェック数を確認
        const checkedWorkUuids = [...enhancedDamWork, ...enhancedPowerpointWork]
            .filter(work => work.notify_checked)
            .map(work => work.uuid);

        if (checkedWorkUuids.length === 0) {
            setAlertMessage("通知対象の業務が選択されていません");
            setAlertSeverity("warning");
            setAlertOpen(true);
            return;
        }
        setNotifyTargetWorkUuids(checkedWorkUuids);
        openNotifyDialog();
    };

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <>
            <BoxCenterStart pb={15}>
                <Grid
                    container
                    spacing={8}
                    columns={12}
                    pt={{ xs: 2, sm: 3, md: 10 }}
                    height={'100%'}
                    width={{ xs: '90%', sm: '80%', md: '80%' }}
                >
                    <CalloutStep
                        title={earthquake ? earthquake.name : ""}
                        step_title={"STEP1: 要員招集"}
                        lead={"地震対応における要員を招集します。要員招集ボタンをクリックし、各従業員に招集SMSを送信してください"}
                        descriptions={[]}
                        useCard={false}
                    >
                        <RegisterButton
                            onClick={onCalloutClick}
                            onCallout={true}
                            startIcon={<Send />}
                            size="large"
                            disabled={isCalloutSent}
                        >
                            要員招集
                        </RegisterButton>
                        <CalloutMemberList
                            calloutUserList={calloutUserList}
                            unanswerUserList={unanswerUserList}
                        />
                    </CalloutStep>
                    <CalloutStep
                        step_title={"STEP2: 業務割当"}
                        lead={"業務割当を行います。各要員に対して業務を割り当ててください。"}
                        descriptions={[damMessage, powerpointMessage].filter(msg => msg !== '')}
                        children={
                            <TaskAssignment
                                handleAssignmentChange={handleAssignmentChange}
                                handleSave={handleSaveClick}
                                handleNotify={handleNotifyClick}
                                damTaskList={enhancedDamWork}
                                powerpointTaskList={enhancedPowerpointWork}
                                noAssignUserList={noAssignUserList}
                                assignedList={assignedList}
                                onNotifyChange={handleNotifyChange}
                                isNotifyDialogOpen={isNotifyDialogOpen}
                            />
                        }
                        useCard={false}
                    >
                    </CalloutStep>
                </Grid>
            </BoxCenterStart>

            <BaseDialog
                open={dialogState.open}
                onClose={closeDialog}
                title={dialogState.title}
                message={dialogState.message}
                onConfirm={handleDialogConfirm}
                confirmText='OK'
                cancelText='キャンセル'
            />

            <InputDialog
                open={isNotifyDialogOpen}
                onClose={closeNotifyDialog}
                title="通知コメント入力"
                message="担当者への通知にコメントを追加できます。"
                onConfirm={() => handleNotify()}
                confirmText='通知送信'
                cancelText='キャンセル'
                errors={{}}
                label="コメント（任意）"
                registerProps={{
                    value: notifyComment,
                    onChange: (e) => setNotifyComment(e.target.value)
                }}
            />

            <Snackbar
                open={alertOpen}
                autoHideDuration={3000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Callout;